import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { LCCConvention, LCCConventionInfo } from "api-client";
import Collapse from "components/Collapse";
import TextInput from "components/TextInput";
import Button from "components/Button";
import ButtonGroups, { BGItem } from "components/ButtonGroups";
import useToast from "hooks/useToast";
import { lccStore } from "./LCCStore";

type FieldKey =
  | "discount_rate_pct"
  | "price_increase_general_pct_pa"
  | "observation_period_years"
  | "maintenance_rate_pct_pa";

const CONVENTION_OPTIONS: { value: LCCConvention; label: string }[] = [
  { value: "dgnb23", label: "DGNB 2023" },
  { value: "bnb", label: "BNB" },
  { value: "qng", label: "QNG" },
];

const CONVENTION_ITEMS: BGItem[] = CONVENTION_OPTIONS.map((opt) => ({
  id: opt.value,
  name: opt.label,
}));

const FIELDS: {
  key: FieldKey;
  labelKey: string;
  tooltipKey: string;
  suffix: string;
}[] = [
  {
    key: "discount_rate_pct",
    labelKey: "discountRate",
    tooltipKey: "discountRateTooltip",
    suffix: "%",
  },
  {
    key: "price_increase_general_pct_pa",
    labelKey: "priceIncreaseGeneral",
    tooltipKey: "priceIncreaseGeneralTooltip",
    suffix: "%/a",
  },
  {
    key: "observation_period_years",
    labelKey: "observationPeriod",
    tooltipKey: "observationPeriodTooltip",
    suffix: "yrs",
  },
  {
    key: "maintenance_rate_pct_pa",
    labelKey: "maintenanceRate",
    tooltipKey: "maintenanceRateTooltip",
    suffix: "%/a",
  },
];

const ConventionSettings = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [draft, setDraft] = useState<LCCConventionInfo | null>(null);
  const [rawValues, setRawValues] = useState<Record<FieldKey, string> | null>(
    null
  );
  const [customMode, setCustomMode] = useState(false);

  const {
    conventionDefaults,
    conventionOverrides,
    selectedConvention,
    loadingConventions,
  } = lccStore;

  const effective = draft ?? conventionDefaults;

  if (!conventionDefaults && !loadingConventions) return null;
  if (loadingConventions && !conventionDefaults) {
    return (
      <div className="mb-6 rounded-lg border border-gray-300 bg-white p-4">
        <div className="h-5 w-48 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  const initFrom = (conv: LCCConventionInfo) => {
    setDraft({ ...conv });
    setRawValues({
      discount_rate_pct: String(conv.discount_rate_pct ?? ""),
      price_increase_general_pct_pa: String(
        conv.price_increase_general_pct_pa ?? ""
      ),
      observation_period_years: String(conv.observation_period_years ?? ""),
      maintenance_rate_pct_pa: String(conv.maintenance_rate_pct_pa ?? ""),
    });
  };

  const appliedValues = conventionOverrides ?? conventionDefaults;
  const isDirty =
    draft != null &&
    appliedValues != null &&
    (draft.discount_rate_pct !== appliedValues.discount_rate_pct ||
      draft.price_increase_general_pct_pa !==
        appliedValues.price_increase_general_pct_pa ||
      draft.observation_period_years !==
        appliedValues.observation_period_years ||
      draft.maintenance_rate_pct_pa !== appliedValues.maintenance_rate_pct_pa);

  const parseAndSync = (key: FieldKey, raw: string) => {
    const num = parseFloat(raw);
    const value = isNaN(num) ? 0 : num;
    if (draft) {
      setDraft({ ...draft, [key]: value });
    }
  };

  const handleFieldChange = (key: FieldKey, raw: string) => {
    if (!rawValues) return;
    setRawValues({ ...rawValues, [key]: raw });
    setCustomMode(true);
  };

  const handleBlur = (key: FieldKey) => {
    if (!rawValues) return;
    const raw = rawValues[key];
    if (raw === "" || raw === "-") {
      setRawValues({ ...rawValues, [key]: "0" });
      parseAndSync(key, "0");
    } else {
      parseAndSync(key, raw);
    }
  };

  const handleApply = () => {
    if (!draft || !rawValues || !isDirty) return;
    const parsed = {
      ...draft,
      discount_rate_pct: parseFloat(rawValues.discount_rate_pct) || 0,
      price_increase_general_pct_pa:
        parseFloat(rawValues.price_increase_general_pct_pa) || 0,
      observation_period_years:
        parseFloat(rawValues.observation_period_years) || 0,
      maintenance_rate_pct_pa:
        parseFloat(rawValues.maintenance_rate_pct_pa) || 0,
    };
    setDraft(parsed);
    lccStore.applyConventionOverrides(parsed);
    toast(t("lcc.conventionApplied"), "success");
  };

  const doConventionChange = (convention: LCCConvention) => {
    lccStore.setSelectedConvention(convention);
    const defaults = lccStore.conventionDefaults;
    if (defaults) {
      initFrom(defaults);
    }
    setCustomMode(false);
    lccStore.applyConventionOverrides(null);
  };

  const description = conventionDefaults?.description;
  const showDescription = description && !customMode;

  const overrideSummary = conventionOverrides
    ? `${conventionOverrides.discount_rate_pct}% · ${conventionOverrides.price_increase_general_pct_pa}%/a · ${conventionOverrides.observation_period_years} yrs · ${conventionOverrides.maintenance_rate_pct_pa}%/a`
    : null;

  return (
    <>
      <Collapse
        containerClassName="mb-6 rounded-lg border border-gray-300 bg-white overflow-hidden"
        headerTitle={
          <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <span>{t("lcc.conventionSettings")}</span>
            {conventionOverrides && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                Custom
              </span>
            )}
          </span>
        }
        closeTitle={`${t("lcc.conventionSettings")} · ${
          conventionOverrides
            ? overrideSummary
            : CONVENTION_OPTIONS.find((o) => o.value === selectedConvention)
                ?.label ?? ""
        }`}
        defaultClose
        onOpen={(state) => {
          if (state) {
            const source = conventionOverrides ?? conventionDefaults!;
            initFrom(source);
            setCustomMode(!!conventionOverrides);
          } else {
            setDraft(null);
            setRawValues(null);
            setCustomMode(false);
          }
        }}
      >
        {effective && (
          <div className="px-4 pb-4">
            <ButtonGroups
              items={CONVENTION_ITEMS}
              current={
                !customMode
                  ? CONVENTION_ITEMS.find((i) => i.id === selectedConvention)
                  : undefined
              }
              onChange={(item) =>
                item && doConventionChange(item.id as LCCConvention)
              }
              hasConfirmation={customMode}
              confirmTitle={t("lcc.discardCustomTitle")}
              confirmDescriptionMesage={t("lcc.discardCustomMessage")}
              confirmButtonTitle={t("lcc.discardConfirm")}
              containerClassName="mt-3 mb-4"
              optionClsssName="px-3 py-1.5"
            />
            {description && (
              <p
                className={`text-xs mb-4 -mt-2 ${
                  showDescription ? "text-gray-500" : "invisible"
                }`}
              >
                {description}
              </p>
            )}
            <div className="flex items-end gap-4 mb-4">
              <div className="grid grid-cols-4 gap-4 flex-1">
                {FIELDS.map(({ key, labelKey, tooltipKey, suffix }) => (
                  <TextInput
                    key={key}
                    labelName={t(`lcc.${labelKey}`)}
                    tooltip={t(`lcc.${tooltipKey}`)}
                    unit={suffix}
                    type="number"
                    step="any"
                    value={rawValues?.[key] ?? effective[key] ?? ""}
                    handleChange={(value) => handleFieldChange(key, value)}
                    onBlur={() => handleBlur(key)}
                    height="small"
                    noMargin
                  />
                ))}
              </div>
              <Button
                type="primary"
                size="small"
                disable={!isDirty}
                onClick={handleApply}
              >
                {t("lcc.apply")}
              </Button>
            </div>
          </div>
        )}
      </Collapse>
    </>
  );
};

export default observer(ConventionSettings);
